import { useCallback, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import client, { setAuthFailureHandler } from "@/api";
import { ArrowUpRight, Bell, Building2, ChevronRight, CircleHelp, ClipboardList, CreditCard, FileText, FileUp, LayoutDashboard, LogOut, Menu, Repeat, Search, Settings2, ShieldCheck, ShieldAlert, Store, WalletCards, X, ScrollText, XCircle, Ban, MapPin, Tags, Package, Receipt, BadgeIndianRupee } from "lucide-react";
import AuthPage from "@/pages/AuthPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import CompanyPage from "@/pages/CompanyPage";
import AdminCompaniesPage from "@/pages/AdminCompaniesPage";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminAuditLogsPage from "@/pages/AdminAuditLogsPage";
import AdminSubscriptionPlansPage from "@/pages/AdminSubscriptionPlansPage";
import AdminCreditLedgerPage from "@/pages/AdminCreditLedgerPage";
import AdminListingsPage from "@/pages/AdminListingsPage";
import AdminTradeRequestsPage from "@/pages/AdminTradeRequestsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import CreditsPage from "@/pages/CreditsPage";
import MyListingsPage from "@/pages/MyListingsPage";
import MarketplacePage from "@/pages/MarketplacePage";
import TradeRequestsPage from "@/pages/TradeRequestsPage";
import TransactionsPage from "@/pages/TransactionsPage";
import AdminTransactionsPage from "@/pages/AdminTransactionsPage";
import BillingPage from "@/pages/BillingPage";
import AdminAccountingPage from "@/pages/AdminAccountingPage";
import AdminInvoicesPage from "@/pages/AdminInvoicesPage";
import AdminTradeDocumentsPage from "@/pages/AdminTradeDocumentsPage";
import DisputesPage from "@/pages/DisputesPage";
import AdminDisputesPage from "@/pages/AdminDisputesPage";
import CounterpartyInvoicesPage from "@/pages/CounterpartyInvoicesPage";
import CircularTradesPage from "@/pages/CircularTradesPage";
import AdminCircularTradesPage from "@/pages/AdminCircularTradesPage";
import AdminReferencePage from "@/pages/AdminReferencePage";
import { TermsPage, PrivacyPage, RefundPage, ShippingPage, ContactPage, GrievancePage, PricingPage, HomePage } from "@/pages/PublicPages";
import AdminWebsiteContentPage from "@/pages/AdminWebsiteContentPage";
import AdminPlatformSettingsPage from "@/pages/AdminPlatformSettingsPage";
import AdminCommercialInvoicesPage from "@/pages/AdminCommercialInvoicesPage";

const roles = { SUPER_ADMIN: "Barter India administrator", OPERATIONS_ADMIN: "Barter India administrator", FINANCE_ADMIN: "Finance administrator", COMPANY_ADMIN: "Company administrator", COMPANY_USER: "Company user" };
const ADMIN_ROLES = new Set(["SUPER_ADMIN", "OPERATIONS_ADMIN", "FINANCE_ADMIN"]);
const COMPANY_ROLES = new Set(["COMPANY_ADMIN", "COMPANY_USER"]);

// Visible-label overrides. The internal nav `key` (used by the workspace
// switch statements, ROUTE_MAPs and page descriptions) is unchanged;
// only the label shown in the sidebar, crumb and page heading is rewritten.
const NAV_LABEL_OVERRIDES = {
  "Counterparty Invoices": "Trade Documents",
};
const displayLabel = (key) => NAV_LABEL_OVERRIDES[key] || key;

const companyNav = [
  { key: "Dashboard", icon: LayoutDashboard },
  { key: "Marketplace", icon: Store },
  { key: "My Listings", icon: FileText },
  { key: "Trade Requests", icon: ClipboardList },
  { key: "Circular Trades", icon: Repeat },
  { key: "Transactions", icon: CreditCard },
  { key: "Disputes", icon: ShieldAlert },
  { key: "Billing", icon: Receipt },
  { key: "Counterparty Invoices", icon: FileText },
  { key: "My Trades", icon: ArrowUpRight },
  { key: "Barter Credits", icon: WalletCards },
  { key: "Company", icon: Building2 },
  { key: "Notifications", icon: Bell },
  { key: "Support", icon: CircleHelp },
];

const adminNav = [
  { key: "Dashboard", icon: LayoutDashboard },
  { key: "Companies", icon: Building2 },
  { key: "Pending Verification", icon: ClipboardList, status: "PENDING_VERIFICATION" },
  { key: "Verified Companies", icon: ShieldCheck, status: "VERIFIED" },
  { key: "Rejected Companies", icon: XCircle, status: "REJECTED" },
  { key: "Suspended Companies", icon: Ban, status: "SUSPENDED" },
  { key: "Subscription Plans", icon: Package },
  { key: "Credit Ledger", icon: WalletCards },
  { key: "Listings", icon: Store },
  { key: "Trade Requests", icon: ClipboardList },
  { key: "Circular Trades", icon: Repeat },
  { key: "Transactions", icon: CreditCard },
  { key: "Disputes", icon: ShieldAlert },
  { key: "Accounting", icon: BadgeIndianRupee },
  { key: "Invoices", icon: Receipt },
  { key: "Counterparty Invoices", icon: FileText },
  { key: "Commercial Invoice Uploads", icon: FileUp },
  { key: "Categories", icon: Tags },
  { key: "Cities", icon: MapPin },
  { key: "Website Content", icon: FileText },
  { key: "Notifications", icon: Bell },
  { key: "Audit Logs", icon: ScrollText },
  { key: "Settings", icon: Settings2 },
];

const STATUS_TONE = { DRAFT: "Draft — complete profile & upload docs", PENDING_VERIFICATION: "Awaiting review", VERIFIED: "Verified", REJECTED: "Needs changes", SUSPENDED: "Suspended" };

function CompanyDashboard({ user, company, creditAccount, onGoToCompany }) {
  const status = company?.status;
  const hasCompany = !!company;
  const hasCredit = !!creditAccount && creditAccount.status !== "PENDING";
  const util = creditAccount?.utilization_percent || 0;
  const needsRegistration = !hasCompany;
  return (
    <>
      <section className="signal-grid">
        <article className="signal-card accent">
          <div className="card-icon"><ShieldCheck size={19} /></div>
          <p className="card-label">ACCOUNT STATUS</p>
          <h3 data-testid="account-status">Authenticated</h3>
          <span>Signed in as {roles[user.role] || user.role}</span>
        </article>
        <article className="signal-card">
          <div className="card-icon"><Building2 size={19} /></div>
          <p className="card-label">COMPANY VERIFICATION</p>
          <h3 data-testid="company-verification-status">{hasCompany ? STATUS_TONE[status] : "Not started"}</h3>
          <span>{hasCompany ? company.legal_name : "Register your company from the Company tab"}</span>
        </article>
        <article className="signal-card">
          <div className="card-icon"><WalletCards size={19} /></div>
          <p className="card-label">BARTER CREDIT</p>
          <h3 data-testid="dashboard-credit-available">{hasCredit ? `₹${creditAccount.available_credit.toLocaleString("en-IN")}` : "—"}</h3>
          <span>{hasCredit ? `${util}% used · ${creditAccount.subscription?.plan_name || "no plan"}` : "Activates after verification and plan assignment"}</span>
        </article>
      </section>
      {hasCredit && (
        <section className="credit-hero compact" data-testid="dashboard-credit-panel">
          <div className="credit-stats-row">
            <div><p className="eyebrow">CREDIT LIMIT</p><strong>₹{creditAccount.current_credit_limit.toLocaleString("en-IN")}</strong></div>
            <div><p className="eyebrow">AVAILABLE</p><strong>₹{creditAccount.available_credit.toLocaleString("en-IN")}</strong></div>
            <div><p className="eyebrow">USED</p><strong>₹{creditAccount.used_credit.toLocaleString("en-IN")}</strong></div>
            <div><p className="eyebrow">UTILIZATION</p><strong>{util}%</strong></div>
            {creditAccount.subscription && <div><p className="eyebrow">PLAN VALIDITY</p><strong>{new Date(creditAccount.subscription.end_date).toLocaleDateString()}</strong></div>}
          </div>
          <div className="progress-bar"><span style={{ width: `${Math.min(100, util)}%` }} /></div>
        </section>
      )}
      <section className="foundation-band">
        <div>
          <p className="eyebrow">NEXT STEP</p>
          <h2>{hasCompany ? (hasCredit ? "Your credit is live — the marketplace opens next." : "Track your verification, upload any missing documents.") : "Register your company to begin the verification review."}</h2>
          <p>Verification decisions and every credit movement are captured in an immutable audit ledger.</p>
        </div>
        <div className="band-list">
          <div
            className={needsRegistration ? "band-step band-step-active" : "band-step"}
            role={needsRegistration ? "button" : undefined}
            tabIndex={needsRegistration ? 0 : undefined}
            onClick={needsRegistration ? onGoToCompany : undefined}
            onKeyDown={needsRegistration ? (e) => {
              if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onGoToCompany && onGoToCompany(); }
            } : undefined}
            data-testid="dashboard-step-register-company"
            aria-label={needsRegistration ? "Register your company — continue to Company tab" : undefined}
          >
            <span>01</span>
            <strong>Register company</strong>
            <small>Business, contact, location, documents</small>
            {needsRegistration && (
              <em className="band-step-cta" data-testid="dashboard-step-register-cta">
                Continue <ChevronRight size={12} />
              </em>
            )}
          </div>
          <div className="band-step"><span>02</span><strong>Verification review</strong><small>Barter India operations approves or requests changes</small></div>
          <div className="band-step"><span>03</span><strong>Plan & credit</strong><small>Admin assigns plan → initial credit issued automatically</small></div>
          <div className="band-step"><span>04</span><strong>Trade</strong><small>Spend and receive credits inside the marketplace</small></div>
        </div>
      </section>
    </>
  );
}

function Placeholder({ label }) {
  return <div className="placeholder-card" data-testid="placeholder-card"><p className="eyebrow">COMING SOON</p><h3>{label}</h3><p className="muted">This surface is scaffolded and will activate in a future phase alongside the marketplace, trade, and credit engines.</p></div>;
}

const COMPANY_ROUTE_MAP = {
  "/": "Dashboard",
  "/marketplace": "Marketplace",
  "/my-listings": "My Listings",
  "/trade-requests": "Trade Requests",
  "/circular-trades": "Circular Trades",
  "/my-trades": "My Trades",
  "/credits": "Barter Credits",
  "/transactions": "Transactions",
  "/billing": "Billing",
  "/counterparty-invoices": "Counterparty Invoices",
  "/disputes": "Disputes",
  "/company": "Company",
  "/notifications": "Notifications",
  "/support": "Support",
};
const ADMIN_ROUTE_MAP = {
  "/admin": "Dashboard",
  "/admin/companies": "Companies",
  "/admin/pending": "Pending Verification",
  "/admin/verified": "Verified Companies",
  "/admin/rejected": "Rejected Companies",
  "/admin/suspended": "Suspended Companies",
  "/admin/subscription-plans": "Subscription Plans",
  "/admin/credit-ledger": "Credit Ledger",
  "/admin/listings": "Listings",
  "/admin/trade-requests": "Trade Requests",
  "/admin/circular-trades": "Circular Trades",
  "/admin/transactions": "Transactions",
  "/admin/accounting": "Accounting",
  "/admin/invoices": "Invoices",
  "/admin/counterparty-invoices": "Counterparty Invoices",
  "/admin/commercial-invoices": "Commercial Invoice Uploads",
  "/admin/disputes": "Disputes",
  "/admin/categories": "Categories",
  "/admin/cities": "Cities",
  "/admin/website-content": "Website Content",
  "/admin/notifications": "Notifications",
  "/admin/audit-logs": "Audit Logs",
  "/admin/settings": "Settings",
};
const routePathFor = (isAdmin, key) => {
  const map = isAdmin ? ADMIN_ROUTE_MAP : COMPANY_ROUTE_MAP;
  return Object.keys(map).find((p) => map[p] === key) || (isAdmin ? "/admin" : "/");
};

function Shell({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const [company, setCompany] = useState(null);
  const [creditAccount, setCreditAccount] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pinnedCompanyId, setPinnedCompanyId] = useState(null);

  const isAdmin = ADMIN_ROLES.has(user.role);
  const nav = isAdmin ? adminNav : companyNav;

  const location = useLocation();
  const navigate = useNavigate();

  // Derive `active` from URL
  const routeMap = isAdmin ? ADMIN_ROUTE_MAP : COMPANY_ROUTE_MAP;
  const active = routeMap[location.pathname] || "Dashboard";
  const setActive = (key) => navigate(routePathFor(isAdmin, key));

  useEffect(() => {
    if (location.pathname.startsWith("/admin") && !isAdmin) navigate("/", { replace: true });
    if (!location.pathname.startsWith("/admin") && isAdmin && location.pathname !== "/login") navigate("/admin", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isAdmin]);

  const loadUnread = () => {
    client.get("/notifications").then((r) => setUnreadCount(r.data.unread_count || 0)).catch(() => setUnreadCount(0));
  };

  const loadCreditAccount = () => {
    client.get("/credits/account").then((r) => setCreditAccount(r.data)).catch(() => setCreditAccount(null));
  };

  useEffect(() => {
    if (!isAdmin) {
      client.get("/companies/me").then((r) => setCompany(r.data || null)).catch(() => setCompany(null));
      loadCreditAccount();
    }
    loadUnread();
    const t = setInterval(loadUnread, 30000);
    return () => clearInterval(t);
  }, [isAdmin]);

  const logout = async () => { await client.post("/auth/logout"); onLogout(); };

  const activeNav = nav.find((n) => n.key === active);

  const openCompanyFromDashboard = (companyId) => {
    setPinnedCompanyId(companyId);
    setActive("Companies");
  };

  const workspace = () => {
    if (isAdmin) {
      if (active === "Dashboard") return <AdminDashboard onOpenCompany={openCompanyFromDashboard} />;
      if (active === "Companies") return <AdminCompaniesPage user={user} initialCompanyId={pinnedCompanyId} onConsumeInitial={() => setPinnedCompanyId(null)} />;
      if (activeNav?.status) return <AdminCompaniesPage user={user} fixedStatus={activeNav.status} hideTabs />;
      if (active === "Subscription Plans") return <AdminSubscriptionPlansPage user={user} />;
      if (active === "Credit Ledger") return <AdminCreditLedgerPage />;
      if (active === "Listings") return <AdminListingsPage user={user} />;
      if (active === "Trade Requests") return <AdminTradeRequestsPage />;
      if (active === "Circular Trades") return <AdminCircularTradesPage />;
      if (active === "Transactions") return <AdminTransactionsPage />;
      if (active === "Accounting") return <AdminAccountingPage />;
      if (active === "Invoices") return <AdminInvoicesPage />;
      if (active === "Counterparty Invoices") return <AdminTradeDocumentsPage />;
      if (active === "Commercial Invoice Uploads") return <AdminCommercialInvoicesPage />;
      if (active === "Disputes") return <AdminDisputesPage />;
      if (active === "Notifications") return <NotificationsPage onChange={loadUnread} />;
      if (active === "Audit Logs") return <AdminAuditLogsPage />;
      if (active === "Categories") return <AdminReferencePage mode="categories" />;
      if (active === "Cities") return <AdminReferencePage mode="cities" />;
      if (active === "Website Content") return <AdminWebsiteContentPage user={user} />;
      if (active === "Settings") return <AdminPlatformSettingsPage />;
      return <Placeholder label={active} />;
    }
    if (active === "Dashboard") return <CompanyDashboard user={user} company={company} creditAccount={creditAccount} onGoToCompany={() => setActive("Company")} />;
    if (active === "Company") return <CompanyPage user={user} onCompanyChange={(c) => { setCompany(c); loadUnread(); loadCreditAccount(); }} />;
    if (active === "Barter Credits") return <CreditsPage />;
    if (active === "Marketplace") return <MarketplacePage user={user} />;
    if (active === "My Listings") return <MyListingsPage user={user} />;
    if (active === "Trade Requests") return <TradeRequestsPage />;
    if (active === "Circular Trades") return <CircularTradesPage />;
    if (active === "Transactions") return <TransactionsPage />;
    if (active === "Billing") return <BillingPage />;
    if (active === "Counterparty Invoices") return <CounterpartyInvoicesPage />;
    if (active === "Disputes") return <DisputesPage />;
    if (active === "Notifications") return <NotificationsPage onChange={loadUnread} />;
    return <Placeholder label={active} />;
  };

  const PAGE_DESCRIPTIONS = {
    "Marketplace": "Discover approved listings from verified companies across categories and cities.",
    "My Listings": "Draft, submit and manage your listings. Approved listings appear on the public marketplace.",
    "Trade Requests": isAdmin
      ? "Review buyer requests, approve or reject with notes, and move them into transactions."
      : "Requests you have raised on other companies' listings, and requests raised on your listings.",
    "Circular Trades": isAdmin
      ? "Approve, authorise settlement and settle multi-party barter cycles."
      : "Multi-party barter opportunities and cycles your company participates in.",
    "Transactions": isAdmin
      ? "Authorise settlement, complete transactions and review the credit ledger effect."
      : "Track fulfilment, confirm receipt, and view settlement status for your trades.",
    "Billing": "Facilitator fee invoices raised on your company. Pay when issued to close each transaction.",
    "Invoices": "Every facilitator fee invoice issued across the platform, with payment status.",
    "Accounting": "Facilitator fee records, invoice lifecycle and reconciliation.",
    "Disputes": isAdmin
      ? "Review, request information and resolve disputes on transactions."
      : "Raise or respond to disputes on your transactions.",
    "Counterparty Invoices": "Underlying commercial tax invoices between seller and buyer (separate from facilitator fees).",
    "Commercial Invoice Uploads": "Seller-uploaded PDF invoices attached to completed transactions.",
    "Listings": "Review draft/submitted listings, approve or reject with notes.",
    "Notifications": "Real-time updates on your account and workflows.",
    "Audit Logs": "Immutable record of every verification status change across all companies.",
    "Barter Credits": "Your current credit limit, used credit and available credit.",
    "Credit Ledger": "Every credit reservation, release, debit and credit across the platform.",
    "Subscription Plans": "Manage subscription tiers and their credit multipliers.",
    "My Trades": "Trades you have participated in as buyer or seller.",
  };
  const eyebrow = isAdmin ? "BARTER INDIA OPERATIONS" : "COMPANY WORKSPACE";
  const description = active === "Dashboard"
    ? isAdmin
      ? "Operations overview: verification queue, recent activity and system status."
      : "A clear view of your workspace, credits, trades and next actions."
    : active === "Company"
      ? "Register your company, upload verification documents, and track review decisions."
      : active === "Companies"
        ? "Approve, reject, suspend or reactivate companies and review their audit history."
        : activeNav?.status
          ? `${STATUS_TONE[activeNav.status]} — filtered view for your workflow.`
          : PAGE_DESCRIPTIONS[active] || `${active} workspace.`;

  return (
    <div className="app-shell">
      <aside className={open ? "sidebar open" : "sidebar"}>
        <div className="sidebar-brand"><span className="brand-mark">B</span><span>Barter India</span>
          <button className="icon-button close-menu" data-testid="close-navigation-button" onClick={() => setOpen(false)}><X size={18} /></button>
        </div>
        <div className="workspace-label">{isAdmin ? "ADMIN CONSOLE" : "COMPANY WORKSPACE"}</div>
        <nav data-testid="primary-navigation">
          {nav.map((item) => {
            const Icon = item.icon;
            const key = item.key.toLowerCase().replaceAll(" ", "-");
            const showBadge = item.key === "Notifications" && unreadCount > 0;
            return (
              <button key={item.key} className={active === item.key ? "nav-item active" : "nav-item"} data-testid={`nav-${key}-button`} onClick={() => { setActive(item.key); setOpen(false); if (item.key === "Notifications") loadUnread(); }}>
                <Icon size={17} /><span>{displayLabel(item.key)}</span>
                {showBadge && <span className="nav-badge" data-testid="notifications-badge">{unreadCount}</span>}
                {active === item.key && !showBadge && <ChevronRight className="nav-arrow" size={14} />}
              </button>
            );
          })}
        </nav>
        <button className="sign-out" data-testid="logout-button" onClick={logout}><LogOut size={17} /> Sign out</button>
      </aside>
      <div className="shell-content">
        <header className="topbar">
          <button className="icon-button menu-button" data-testid="open-navigation-button" onClick={() => setOpen(true)}><Menu size={20} /></button>
          <div className="crumb"><span>{isAdmin ? "Admin Console" : "Workspace"}</span><ChevronRight size={14} /><strong>{displayLabel(active)}</strong></div>
          <div className="top-actions">
            <button className="icon-button" data-testid="notifications-button" onClick={() => { setActive("Notifications"); loadUnread(); }}>
              <Bell size={18} />
              {unreadCount > 0 && <span className="notification-dot" data-testid="notifications-dot" />}
            </button>
            <div className="user-chip" data-testid="current-user-summary">
              <span className="avatar">{user.name?.charAt(0) || "A"}</span>
              <span className="user-info"><strong>{user.name}</strong><small>{roles[user.role] || user.role}</small></span>
            </div>
          </div>
        </header>
        <main className="workspace" data-testid="workspace-content">
          <div className="page-heading">
            <div>
              <p className="eyebrow">{eyebrow}</p>
              <h1>{displayLabel(active)}</h1>
              <p className="muted" data-testid="page-description">{description}</p>
            </div>
            <div className="date-pill" data-testid="workspace-status"><span className="status-dot">●</span> Live</div>
          </div>
          {workspace()}
        </main>
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [authUnavailable, setAuthUnavailable] = useState(false);
  const checkSession = useCallback(async () => {
    setChecking(true);
    setAuthUnavailable(false);
    try {
      const response = await client.get("/auth/me");
      setUser(response.data);
    } catch (error) {
      if (error?.response?.status === 401) setUser(null);
      else setAuthUnavailable(true);
    } finally {
      setChecking(false);
    }
  }, []);
  useEffect(() => {
    const removeHandler = setAuthFailureHandler(() => {
      setUser(null);
      setAuthUnavailable(false);
    });
    checkSession();
    return removeHandler;
  }, [checkSession]);
  if (checking) return <div className="loading-screen" data-testid="auth-loading">Loading workspace…</div>;
  if (authUnavailable) return (
    <div className="loading-screen" data-testid="auth-unavailable">
      <p>We could not verify your session. Check your connection and try again.</p>
      <button type="button" className="primary-button" data-testid="auth-retry-button" onClick={checkSession}>Retry</button>
    </div>
  );
  const isAdmin = user && ADMIN_ROLES.has(user.role);
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/refund" element={<RefundPage />} />
        <Route path="/shipping" element={<ShippingPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/grievance" element={<GrievancePage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/" element={
          user
            ? (isAdmin ? <Navigate to="/admin" replace /> : <Shell user={user} onLogout={() => setUser(null)} />)
            : <HomePage />
        } />
        <Route path="/login" element={user ? <Navigate to={isAdmin ? "/admin" : "/"} replace /> : <AuthPage onAuth={setUser} />} />
        <Route path="/forgot-password" element={user ? <Navigate to={isAdmin ? "/admin" : "/"} replace /> : <ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/admin/*" element={
          user
            ? (isAdmin ? <Shell user={user} onLogout={() => setUser(null)} /> : <Navigate to="/" replace />)
            : <Navigate to="/login" replace />
        } />
        <Route path="*" element={
          user
            ? (isAdmin ? <Navigate to="/admin" replace /> : <Shell user={user} onLogout={() => setUser(null)} />)
            : <Navigate to="/" replace />
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
